import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Design } from '@/lib/design/types'

import Link from 'next/link'
import {
    ArrowRight,
    Check,
    ChevronDown,
    LayoutGrid,
    Link2,
    MessageSquareText,
    Palette,
    PenTool,
    Sparkles,
    Users,
    Wand2
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PhoneMock } from '@/components/landing/phone-mock'
import { UrlForm } from '@/components/landing/url-form'
import { SlidePreview } from '@/components/shared/slide-preview'
import { sampleDesign } from '@/lib/design/samples'
import { THEMES } from '@/lib/design/themes'

interface HomeProps {
    hero: Design
    gallery: Array<{ id: string, name: string, description: string, design: Design }>
    planned: Array<Design>
}

const STEPS = [
    {
        icon: Link2,
        title: 'Paste a link',
        text: 'Drop in any public blog post. Feedsmith reads the article and pulls out its structure and strongest ideas.'
    },
    {
        icon: Wand2,
        title: 'Polish it in the designer',
        text: 'You get a cover hook, one idea per slide and a closing call to action. Change any word, font, color or picture.'
    },
    {
        icon: LayoutGrid,
        title: 'Plan it in your grid',
        text: 'Add the post to a copy of your profile, next to the posts you already have, and drag it until the grid looks right.'
    }
]

const FEATURES = [
    { icon: Sparkles, title: 'Hooks worth stopping for', text: 'Three cover hooks for every article, built on curiosity and a clear benefit, written in the article’s own language.' },
    { icon: PenTool, title: 'A real designer', text: 'Drag, resize and rotate anything, highlight key words, swap fonts, photos and backgrounds, and undo any step.' },
    { icon: MessageSquareText, title: 'Captions with the rules built in', text: 'A 2,200 character counter, a preview of the line shown before "more" and a cap of 5 hashtags, Instagram’s current limit.' },
    { icon: LayoutGrid, title: 'Feed simulator', text: 'See posts in the 3:4 profile grid, pin up to three, and flip between today’s grid and the planned one.' },
    { icon: Users, title: 'As many feeds as you need', text: 'One mock feed per account or client, each with its own handle, bio, avatar and follower count.' },
    { icon: Palette, title: 'Brand kit', text: 'Your handle, logo, colors and fonts land on every new post, and restyle an older one in a single click.' }
]

const FACTS = [
    { value: '20', label: 'slides per carousel' },
    { value: '3', label: 'post sizes, including 3:4' },
    { value: '8', label: 'themes to start from' },
    { value: 'PNG + ZIP', label: 'downloads with caption' }
]

const FAQS = [
    {
        question: 'Does Feedsmith post to Instagram for me?',
        answer: 'No. You download the slides and copy the caption, then post from the Instagram app as usual. The feed simulator shows you exactly how the post will look before you do.'
    },
    {
        question: 'Which blogs does it work with?',
        answer: 'Most public articles, from WordPress, Substack, Medium, Ghost, Webflow, Squarespace or your own site. If a page needs a login or blocks reading tools, paste the article text instead.'
    },
    {
        question: 'What sizes can I make?',
        answer: 'Square 1080 x 1080, portrait 1080 x 1350 and tall 1080 x 1440, which fills the 3:4 profile grid tile exactly. Carousels can have up to 20 slides.'
    },
    {
        question: 'Can I write posts in my own language?',
        answer: 'Yes. Posts are written in the language of the article, and every word stays editable in the designer.'
    },
    {
        question: 'How many hashtags should I use?',
        answer: 'Instagram now allows up to five per post. Feedsmith suggests five specific ones and warns you when a caption goes over.'
    },
    {
        question: 'Who can see my posts and feeds?',
        answer: 'Only you. Posts, feeds and pictures are tied to your account and never shown to anyone else.'
    }
]

const PHOTOS = ['/images/feed-1.jpg', '/images/feed-2.jpg', '/images/feed-3.png', '/images/feed-4.jpg', '/images/feed-5.png', '/images/feed-6.png']

const Home: FC<HomeProps> = ({ hero, gallery, planned }): ReactNode => {
    return (
        <Layout>
            <Seo description='Turn any blog post into an Instagram carousel people save. Paste a link, polish every slide in a built-in designer and see the post in your profile grid before you share it.' />

            <section id='hero' className='relative overflow-hidden'>
                <div className='pointer-events-none absolute -top-40 -right-40 size-[520px] rounded-full bg-coral/15 blur-3xl' aria-hidden='true' />
                <div className='pointer-events-none absolute top-60 -left-40 size-[420px] rounded-full bg-sun/20 blur-3xl' aria-hidden='true' />

                <div className='relative mx-auto grid max-w-6xl items-center gap-14 px-5 pt-14 pb-20 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:pt-20 lg:pb-28'>
                    <div className='flex flex-col gap-7'>
                        <p className='inline-flex w-fit items-center gap-2 rounded-full border border-border bg-card px-3.5 py-1.5 text-sm font-medium'>
                            <span className='size-2 rounded-full bg-coral' aria-hidden='true' />
                            Blog post to Instagram carousel, in one paste
                        </p>

                        <h1 className='text-5xl leading-[1.02] font-extrabold sm:text-6xl lg:text-[4.4rem]'>
                            Turn any blog post into a carousel people <span className='relative inline-block text-primary'>save<svg className='absolute -bottom-2 left-0 w-full text-coral' viewBox='0 0 200 12' fill='none' aria-hidden='true'><path d='M2 9C50 3 150 3 198 9' stroke='currentColor' strokeWidth='4' strokeLinecap='round' /></svg></span>.
                        </h1>

                        <p className='max-w-xl text-lg leading-relaxed text-muted-foreground'>
                            Paste a link. Feedsmith writes the hook, the slides and the caption, lets you style every detail in a built-in designer, and shows exactly how the post sits in your grid before you share it.
                        </p>

                        <UrlForm id='hero-url' className='max-w-xl' />

                        <ul className='flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground'>
                            {['No design skills needed', 'Works with most public blogs', 'Download as PNG or ZIP'].map(item => (
                                <li key={item} className='flex items-center gap-2'>
                                    <Check className='size-4 text-primary' />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className='relative mx-auto h-[420px] w-full max-w-[460px] sm:h-[500px]' aria-label='Three slides of an example carousel' role='img'>
                        <div className='fs-float absolute top-10 left-0 w-[46%] overflow-hidden rounded-2xl shadow-2xl ring-1 ring-black/5 [--fs-rotate:-8deg]' style={{ transform: 'rotate(-8deg)' }}>
                            <SlidePreview design={hero} index={1} width={200} fluid />
                        </div>
                        <div className='fs-float absolute top-12 right-0 w-[46%] overflow-hidden rounded-2xl shadow-2xl ring-1 ring-black/5 [--fs-rotate:8deg] [animation-delay:1.2s]' style={{ transform: 'rotate(8deg)' }}>
                            <SlidePreview design={hero} index={2} width={200} fluid />
                        </div>
                        <div className='absolute top-0 left-1/2 w-[58%] -translate-x-1/2 overflow-hidden rounded-2xl shadow-[0_30px_80px_-20px_rgba(28,24,20,0.55)] ring-1 ring-black/5'>
                            <SlidePreview design={hero} index={0} width={260} fluid />
                        </div>
                        <div className='absolute bottom-2 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full bg-card px-4 py-2 text-sm font-medium shadow-lg ring-1 ring-border'>
                            <Sparkles className='size-4 text-primary' />
                            Written from one blog post
                        </div>
                    </div>
                </div>
            </section>

            <section id='how-it-works' className='border-y border-border bg-card'>
                <div className='mx-auto max-w-6xl px-5 py-20 sm:px-8'>
                    <div className='max-w-2xl'>
                        <p className='text-sm font-semibold tracking-wide text-primary uppercase'>How it works</p>
                        <h2 className='mt-3 text-4xl font-bold sm:text-5xl'>From article to grid in three steps</h2>
                    </div>

                    <ol className='mt-12 grid gap-6 md:grid-cols-3'>
                        {STEPS.map((step, index) => (
                            <li key={step.title} className='relative flex flex-col gap-4 rounded-3xl bg-background p-6'>
                                <span className='flex items-center justify-between'>
                                    <span className='flex size-12 items-center justify-center rounded-2xl bg-ink text-paper'>
                                        <step.icon className='size-6' />
                                    </span>
                                    <span className='font-heading text-5xl font-extrabold text-border'>0{index + 1}</span>
                                </span>
                                <h3 className='text-xl font-semibold'>{step.title}</h3>
                                <p className='leading-relaxed text-muted-foreground'>{step.text}</p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            <section id='features' className='mx-auto max-w-6xl px-5 py-24 sm:px-8'>
                <div className='flex flex-col gap-4 md:flex-row md:items-end md:justify-between'>
                    <div className='max-w-2xl'>
                        <p className='text-sm font-semibold tracking-wide text-primary uppercase'>Features</p>
                        <h2 className='mt-3 text-4xl font-bold sm:text-5xl'>Everything between your blog and your feed</h2>
                    </div>
                    <p className='max-w-sm text-muted-foreground'>
                        Write it, design it and plan it in one place, without jumping between five apps.
                    </p>
                </div>

                <div className='mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3'>
                    {FEATURES.map(feature => (
                        <article key={feature.title} className='flex flex-col gap-3 rounded-3xl border border-border bg-card p-6 transition-shadow hover:shadow-md'>
                            <span className='flex size-11 items-center justify-center rounded-xl bg-accent text-accent-foreground'>
                                <feature.icon className='size-5' />
                            </span>
                            <h3 className='text-lg font-semibold'>{feature.title}</h3>
                            <p className='leading-relaxed text-muted-foreground'>{feature.text}</p>
                        </article>
                    ))}
                </div>
            </section>

            <section id='templates' className='bg-ink text-paper'>
                <div className='mx-auto max-w-6xl px-5 py-24 sm:px-8'>
                    <div className='flex flex-col gap-4 md:flex-row md:items-end md:justify-between'>
                        <div className='max-w-2xl'>
                            <p className='text-sm font-semibold tracking-wide text-coral uppercase'>Templates</p>
                            <h2 className='mt-3 text-4xl font-bold sm:text-5xl'>Eight looks, one click apart</h2>
                        </div>
                        <p className='max-w-sm text-paper/70'>
                            Switch the theme and every slide restyles itself. Your words stay exactly where they are.
                        </p>
                    </div>

                    <ul className='mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4'>
                        {gallery.map(item => (
                            <li key={item.id} className='flex flex-col gap-3'>
                                <div className='overflow-hidden rounded-2xl ring-1 ring-paper/10'>
                                    <SlidePreview design={item.design} index={0} width={260} fluid label={`${item.name} theme example`} />
                                </div>
                                <div>
                                    <h3 className='font-sans text-base font-semibold tracking-normal'>{item.name}</h3>
                                    <p className='text-sm text-paper/60'>{item.description}</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section id='simulator' className='mx-auto grid max-w-6xl items-center gap-14 px-5 py-24 sm:px-8 lg:grid-cols-2'>
                <PhoneMock planned={planned} photos={PHOTOS} />

                <div className='flex flex-col gap-6'>
                    <p className='text-sm font-semibold tracking-wide text-primary uppercase'>Feed simulator</p>
                    <h2 className='text-4xl font-bold sm:text-5xl'>See it in your grid before your followers do</h2>
                    <p className='text-lg leading-relaxed text-muted-foreground'>
                        A carousel can look great on its own and still clash with the rest of your profile. Plan it next to the posts you already have.
                    </p>
                    <ul className='flex flex-col gap-3'>
                        {[
                            'Upload the posts already on your profile',
                            'Drop planned posts into the newest spot',
                            'Drag to reorder and pin up to three posts',
                            'Flip between today’s grid and the planned one',
                            'Check the 3:4 tile crop and the classic square grid'
                        ].map(item => (
                            <li key={item} className='flex items-start gap-3'>
                                <span className='mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground'>
                                    <Check className='size-4' />
                                </span>
                                {item}
                            </li>
                        ))}
                    </ul>
                    <Link href='/signup' className={cn(buttonVariants(), 'h-12 w-fit rounded-full px-6 text-base')}>
                        Plan my grid
                        <ArrowRight className='size-5' />
                    </Link>
                </div>
            </section>

            <section aria-label='Feedsmith in numbers' className='border-y border-border bg-card'>
                <dl className='mx-auto grid max-w-6xl grid-cols-2 gap-8 px-5 py-14 sm:px-8 md:grid-cols-4'>
                    {FACTS.map(fact => (
                        <div key={fact.label} className='flex flex-col gap-1'>
                            <dt className='order-2 text-sm text-muted-foreground'>{fact.label}</dt>
                            <dd className='order-1 font-heading text-4xl font-extrabold'>{fact.value}</dd>
                        </div>
                    ))}
                </dl>
            </section>

            <section id='faq' className='mx-auto max-w-3xl px-5 py-24 sm:px-8'>
                <p className='text-sm font-semibold tracking-wide text-primary uppercase'>FAQ</p>
                <h2 className='mt-3 text-4xl font-bold sm:text-5xl'>Questions people ask first</h2>

                <div className='mt-10 flex flex-col divide-y divide-border rounded-3xl border border-border bg-card'>
                    {FAQS.map(item => (
                        <details key={item.question} className='group px-6 py-5'>
                            <summary className='flex cursor-pointer list-none items-center justify-between gap-4 text-lg font-semibold [&::-webkit-details-marker]:hidden'>
                                {item.question}
                                <ChevronDown className='size-5 shrink-0 transition-transform group-open:rotate-180' />
                            </summary>
                            <p className='mt-3 leading-relaxed text-muted-foreground'>{item.answer}</p>
                        </details>
                    ))}
                </div>
            </section>

            <section id='get-started' className='px-5 pb-24 sm:px-8'>
                <div className='relative mx-auto max-w-6xl overflow-hidden rounded-[40px] bg-ink px-6 py-16 text-paper sm:px-14'>
                    <div className='pointer-events-none absolute -right-20 -bottom-24 size-80 rounded-full bg-coral/30 blur-3xl' aria-hidden='true' />
                    <div className='relative flex max-w-2xl flex-col gap-6'>
                        <h2 className='text-4xl font-bold sm:text-5xl'>Your next post is already written. It is on your blog.</h2>
                        <p className='text-lg text-paper/75'>
                            Paste a link and have a carousel ready to download in minutes.
                        </p>
                        <UrlForm id='footer-url' dark />
                        <Link href='/signup?next=%2Fcreate' className='w-fit text-sm font-medium text-paper/80 underline underline-offset-4 hover:text-paper'>
                            Or start from a template
                        </Link>
                    </div>
                </div>
            </section>
        </Layout>
    )
}

const plain = <T, >(value: T): T => JSON.parse(JSON.stringify(value)) as T

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    const heroDesign = sampleDesign('ink', '4:5')
    const hero = { ...heroDesign, slides: [heroDesign.slides[0], heroDesign.slides[1], heroDesign.slides[2]] }

    const gallery = THEMES.map(theme => {
        const design = sampleDesign(theme.id, '4:5')

        return { id: theme.id, name: theme.name, description: theme.description, design: { ...design, slides: design.slides.slice(0, 2) } }
    })

    const planned = (['citrus', 'paper', 'photo-overlay'] as const).map(themeId => {
        const design = sampleDesign(themeId, '3:4')

        return { ...design, slides: design.slides.slice(0, 2) }
    })

    return { props: plain({ hero, gallery, planned }) }
}

export default Home
